import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  resellingConnectAccountApi,
  resellingAccountStatusApi,
  resellingDisconnectApi,
  resellingConfigApi,
  updateCheckinConfigApi,
  updateCreditConfigApi,
  resellingClientsPaginatedApi,
  resellingOverviewStatsApi,
  resellingDashboardLinkApi,
} from "../../apis/UserApis/ResellingApi";
import { showSnackbar } from "../../slices/SnackbarSlice";

export const resellingConnectAccountAction = createAsyncThunk(
  "reselling/connect-account",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await resellingConnectAccountApi();
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Could not generate Stripe onboarding link",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data || { message: "Could not connect Stripe" },
      );
    }
  },
);

export const resellingAccountStatusAction = createAsyncThunk(
  "reselling/account-status",
  async (_, { rejectWithValue }) => {
    try {
      const response = await resellingAccountStatusApi();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not get account status" },
      );
    }
  },
);

export const resellingDisconnectAction = createAsyncThunk(
  "reselling/disconnect",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await resellingDisconnectApi();
      dispatch(
        showSnackbar({
          message:
            response?.data?.message ||
            "Stripe account disconnected successfully",
          type: "success",
        }),
      );
      return response.data;
    } catch (error: any) {
      const errorData = error?.response?.data;
      const errorMessage =
        errorData?.error ||
        errorData?.message ||
        "Could not disconnect Stripe account";
      dispatch(
        showSnackbar({
          message: errorMessage,
          type: "error",
        }),
      );
      return rejectWithValue(
        errorData || { message: "Could not disconnect Stripe account" },
      );
    }
  },
);

export const resellingConfigAction = createAsyncThunk(
  "reselling/config",
  async (_, { rejectWithValue }) => {
    try {
      const response = await resellingConfigApi();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not fetch reseller config" },
      );
    }
  },
);

export const updateCheckinConfigAction = createAsyncThunk(
  "reselling/update-checkin-config",
  async (
    payload: {
      checkin_enabled: boolean;
      resale_price_cents: number;
      trial_days: number;
      require_payment_upfront: boolean;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await updateCheckinConfigApi(payload);
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Could not update check-in config",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data || {
          message: "Could not update check-in config",
        },
      );
    }
  },
);

export const updateCreditConfigAction = createAsyncThunk(
  "reselling/update-credit-config",
  async (
    payload: {
      credits_enabled: boolean;
      markup_multiplier: number;
      credits_per_action: number;
    },
    { rejectWithValue, dispatch },
  ) => {
    try {
      const response = await updateCreditConfigApi(payload);
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Could not update credit config",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data || { message: "Could not update credit config" },
      );
    }
  },
);

export const resellingClientsPaginatedAction = createAsyncThunk(
  "reselling/clients-paginated",
  async (
    payload:
      | {
          page?: number;
          limit?: number;
          search?: string;
          sort_by?: string;
          sort_order?: string;
          status?: string;
        }
      | undefined,
    { rejectWithValue },
  ) => {
    try {
      const response = await resellingClientsPaginatedApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not fetch reselling clients" },
      );
    }
  },
);

export const resellingOverviewStatsAction = createAsyncThunk(
  "reselling/overview-stats",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await resellingOverviewStatsApi(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data || { message: "Could not fetch reselling overview stats" },
      );
    }
  },
);

export const resellingDashboardLinkAction = createAsyncThunk(
  "reselling/dashboard-link",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response = await resellingDashboardLinkApi();
      return response.data;
    } catch (error: any) {
      dispatch(
        showSnackbar({
          message:
            error?.response?.data?.error ||
            error?.response?.data?.message ||
            "Could not fetch Stripe Express dashboard link",
          type: "error",
        }),
      );
      return rejectWithValue(
        error?.response?.data || { message: "Could not fetch dashboard link" },
      );
    }
  },
);
